import i18n from 'i18next';
import { isEqual, memoize } from 'lodash';
import {
  type AddItemFailedEvent,
  type CanvasClickEvent,
  type OpenFailedEvent,
  type TileDrawingEvent,
  type TileLoadFailedEvent,
} from 'openseadragon4';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { type RotationTarget } from 'horizon/routes/Inspections/types';
import { notifyBugsnag } from 'utils/bugsnag';
import { getXYBounds } from 'utils/geo';
import { usePrevious } from 'utils/hooks';
import OpenSeadragon, {
  type OSD4ViewerWithSVG,
  CLICK_ZOOM_FACTOR,
  getOpenSeadragon4Viewer,
  getTileSources,
} from 'utils/openseadragon4';
import { useSignedUrlsExpiredMessaging } from 'utils/signed-urls';

import { Controls } from './Controls';
import { type ImageProps } from './Image';
import { ImageSvgOverlay } from './ImageSvgOverlay';
import { ImageContainer, ThumbnailHeader } from './ImageViewer.style';
import { getRotationDegrees, isRotationTarget } from './utils';

type RotationMode = 'none' | 'blade' | 'custom';

export const Image: React.FunctionComponent<ImageProps> = ({
  isNewFullSizeImageViewer = false,
  isThumbnail = false,
  setObservation,
  filter,
  setFilter,
  pictureId,
  pictureGlobalId,
  image,
  defaultValue = {},
  doAjaxLoad = false,
  observations = [],
  target,
  render = () => null,
  primaryIndicator,
  thumbnailHeaderAttributes,
  onTileLoadFailed,
  measurementsRender,
  showMeasurements,
  setShowMeasurements,
  temperaturesRender,
  showTemperatures,
  setShowTemperatures,
  assetOverlayRender,
  showAssetOverlay,
  setShowAssetOverlay,
  inferenceFilterOptions = [],
  selectedInferenceDetectionTypeIds,
  setSelectedInferenceDetectionTypeIds,
  deltaTMeasurementRender,
  showDeltaTMeasurement,
  showInferenceOverlay,
  isEdit,
  hasHomography,
  northRotationDeg,
  rotationDisabled,
  refetch,
  inferenceBoundaries = [],
  panelDetectionBoundaries = [],
  onInferenceBoundaryClick,
}) => {
  const { state } = useLocation<{ useModal?: boolean }>();
  const [viewer, setViewer] = useState<OSD4ViewerWithSVG | undefined>();
  const [svg, setSvg] = useState<SVGElement>();
  const [rotationMode, setRotationMode] = useState<RotationMode>('none');
  const [customRotationDeg, setCustomRotationDeg] = useState(0);
  const [levels, setLevels] = useState<{ a?: number; b?: number }>();
  const [imageContainer, setImageContainer] = useState<HTMLDivElement | null>(null);
  const observationClickDisabled = useRef<boolean>(false);

  const componentRotationDegrees = isRotationTarget(target)
    ? getRotationDegrees(target as RotationTarget)
    : 0;
  const hasNorthRotation = typeof northRotationDeg === 'number';

  const rotationEnabled =
    !rotationDisabled &&
    (isRotationTarget(target) ? componentRotationDegrees !== 0 : hasNorthRotation);

  const bladeRotationTitle = isRotationTarget(target)
    ? i18n.t('components.rotate', 'Rotate')
    : hasNorthRotation
      ? i18n.t('images.alignToNorth', 'Align to north')
      : i18n.t('components.rotate', 'Rotate');

  const setViewportRotation = useCallback(
    (degrees: number) => {
      viewer?.viewport?.setRotation(degrees);
    },
    [viewer]
  );

  const getBladeRotationDegrees = useCallback((): number => {
    if (isRotationTarget(target)) {
      return getRotationDegrees(target as RotationTarget);
    }

    if (typeof northRotationDeg === 'number') {
      return northRotationDeg;
    }

    return 0;
  }, [northRotationDeg, target]);

  const applyCurrentRotation = useCallback(() => {
    if (!viewer || rotationDisabled) {
      setViewportRotation(0);
      return;
    }

    if (rotationMode === 'custom') {
      setViewportRotation(customRotationDeg);
      return;
    }

    if (rotationMode === 'blade') {
      setViewportRotation(getBladeRotationDegrees());
      return;
    }

    setViewportRotation(0);
  }, [
    customRotationDeg,
    getBladeRotationDegrees,
    rotationDisabled,
    rotationMode,
    setViewportRotation,
    viewer,
  ]);

  const applyCustomRotation = useCallback(
    (degrees: number) => {
      if (!viewer) {
        return;
      }

      if (degrees === 0) {
        setRotationMode('none');
        setCustomRotationDeg(0);
        setViewportRotation(0);
        return;
      }

      setRotationMode('custom');
      setCustomRotationDeg(degrees);
      setViewportRotation(degrees);
    },
    [setViewportRotation, viewer]
  );

  const resetCustomRotation = useCallback(() => {
    setRotationMode('none');
    setCustomRotationDeg(0);
    setViewportRotation(0);
  }, [setViewportRotation]);

  // update the ref everytime setObservation changes
  const setObservationRef = useRef(setObservation);
  const onInferenceBoundaryClickRef = useRef(onInferenceBoundaryClick);
  const onTileLoadFailedRef = useRef(onTileLoadFailed);
  useLayoutEffect(() => {
    setObservationRef.current = setObservation;
  }, [setObservation]);
  useLayoutEffect(() => {
    onInferenceBoundaryClickRef.current = onInferenceBoundaryClick;
  }, [onInferenceBoundaryClick]);
  useLayoutEffect(() => {
    onTileLoadFailedRef.current = onTileLoadFailed;
  }, [onTileLoadFailed]);

  const zoomViewIn = useCallback(() => {
    const viewport = viewer?.viewport;
    if (viewport) {
      const zoom = viewport.getZoom() * CLICK_ZOOM_FACTOR;
      viewport.zoomTo(zoom);
    }
  }, [viewer]);

  const zoomViewOut = useCallback(() => {
    const viewport = viewer?.viewport;
    if (viewport) {
      const zoom = viewport.getZoom() * (1 / CLICK_ZOOM_FACTOR);
      viewport.zoomTo(zoom);
    }
  }, [viewer]);

  const resetZoom = () => {
    viewer?.viewport?.goHome();
  };

  const toggleRotate = useCallback(
    (useRotate: boolean) => {
      if (rotationDisabled || !viewer) {
        return;
      }

      if (useRotate) {
        setRotationMode('blade');
        setCustomRotationDeg(0);
        setViewportRotation(getBladeRotationDegrees());
        return;
      }

      setRotationMode('none');
      setCustomRotationDeg(0);
      setViewportRotation(0);
    },
    [getBladeRotationDegrees, rotationDisabled, setViewportRotation, viewer]
  );

  const handleControls = ({
    zoomIn,
    zoomOut,
    reset,
    rotate,
    customRotationDeg: nextCustomRotationDeg,
    customRotationReset,
    filter,
    measure,
    temperature,
    assetOverlay,
    selectedInferenceDetectionTypeIds,
  }: {
    zoomIn?: boolean;
    zoomOut?: boolean;
    reset?: boolean;
    rotate?: boolean;
    customRotationDeg?: number;
    customRotationReset?: boolean;
    filter?: string;
    measure?: boolean;
    temperature?: boolean;
    assetOverlay?: boolean;
    selectedInferenceDetectionTypeIds?: string[];
  }) => {
    zoomIn && zoomViewIn();
    zoomOut && zoomViewOut();
    reset && resetZoom();
    rotate !== undefined && toggleRotate(rotate);
    customRotationReset && resetCustomRotation();
    nextCustomRotationDeg !== undefined && applyCustomRotation(nextCustomRotationDeg);
    filter !== undefined && setFilter && setFilter(filter);
    measure !== undefined && setShowMeasurements?.(measure);
    temperature !== undefined && setShowTemperatures?.(temperature);
    assetOverlay !== undefined && setShowAssetOverlay?.(assetOverlay);
    selectedInferenceDetectionTypeIds !== undefined &&
      setSelectedInferenceDetectionTypeIds?.(selectedInferenceDetectionTypeIds);
  };

  const handleObservationClick = (observationId?: string) => {
    if (setObservation && observationId && !observationClickDisabled.current) {
      setObservation(observationId);
    }
  };

  // This will apply the linear transformation declared by a "Level" control
  const transformImage = memoize((context: CanvasRenderingContext2D) => {
    const image = context.getImageData(0, 0, context.canvas.width, context.canvas.height);
    const originalData = new Uint8ClampedArray(image.data);

    return ({ a = 1, b = 0 } = {}) => {
      // Prevent repaints using a key
      const key = [a, b].toString();

      if (key === context.canvas.id) {
        return image;
      }

      if (a === 1 && b === 0) {
        image.data.set(originalData);
      } else {
        const data = new Uint8ClampedArray(originalData);

        for (let i = 0; i < data.length; i += 4) {
          for (let j = 0; j < 3; ++j) {
            data[i + j] = a * data[i + j] + b;
          }
        }

        image.data.set(data);
      }

      context.canvas.id = key;

      return image;
    };
  });

  // debounced to allow viewer to initialize
  const focusLayer = useDebouncedCallback(() => {
    const { coordinates } = observations.find(o => o.selected) || {};

    if (!coordinates) {
      return;
    }

    // Zoom to a point where the observation fits in the window, with a little breathing room
    const [[xMin, yMax], [xMax, yMin]] = getXYBounds(coordinates);
    const width = xMax - xMin;
    const height = yMax - yMin;
    const location = new OpenSeadragon.Rect(
      xMin - 0.1 * width,
      yMin - 0.2 * height,
      1.2 * width,
      1.4 * height
    );

    // ensures tab is in focus when moving the viewport
    window.requestAnimationFrame(() => {
      viewer?.viewport?.fitBoundsWithConstraints(location);
    });
  }, 300);

  const dziImage = image?.signedDziSchema?.dzi?.Image;

  // only set the expired messaging when the image viewer is visited directly
  // and not when it's been manually invoked by a user clicking on a thumbnail
  // image (e.g. from the inspection or damage details page). this will help
  // ensure that the messaging is not invoked twice.
  //
  // since this uses the `image.signedDziSchema` expiration and the details
  // pages use the `image.signedUrl` expiration, it's assumed these values are
  // the same, which we enforce in our GQL schema query.
  const expiresAt = !state?.useModal ? dziImage?.expiresAt : 0;
  useSignedUrlsExpiredMessaging(expiresAt);

  // intializes the openseadragon viewer
  useEffect(() => {
    const handleZoomKeys = ({ target, code }: { target: any; code: string }) => {
      if (target.form === undefined) {
        switch (code) {
          case 'Minus':
            return zoomViewOut;
          case 'Equal':
            return zoomViewIn;
          default:
            break;
        }
      }
    };

    if (imageContainer !== null) {
      // set initial rotation based on blade angle or north rotation
      const degrees = rotationDisabled
        ? 0
        : isRotationTarget(target)
          ? componentRotationDegrees
          : hasNorthRotation
            ? (northRotationDeg as number)
            : 0;
      const initialRotationMode: RotationMode =
        !rotationDisabled &&
        (isRotationTarget(target) ? componentRotationDegrees !== 0 : hasNorthRotation)
          ? 'blade'
          : 'none';
      setRotationMode(initialRotationMode);
      setCustomRotationDeg(0);

      const osdViewer = getOpenSeadragon4Viewer({
        degrees,
        doAjaxLoad,
        dziImage,
        element: imageContainer,
      });

      setViewer(osdViewer);
      window.addEventListener('keydown', handleZoomKeys);
    }

    return () => {
      if (viewer) {
        viewer.destroy();
        setViewer(undefined);
      }

      window.removeEventListener('keydown', handleZoomKeys);
    };

    // when providing all the dependencies required, the thumbnail viewer will
    // "lose" the image after viewing the image modal. there is probably a
    // better solution than masking the eslint warning, but not today...
    // eslint-disable-next-line
  }, [imageContainer]);

  useEffect(() => {
    if (!viewer) {
      return;
    }

    if (rotationDisabled) {
      setViewportRotation(0);
      if (rotationMode !== 'none' || customRotationDeg !== 0) {
        setRotationMode('none');
        setCustomRotationDeg(0);
      }
      return;
    }

    applyCurrentRotation();
  }, [
    applyCurrentRotation,
    componentRotationDegrees,
    customRotationDeg,
    hasNorthRotation,
    northRotationDeg,
    rotationDisabled,
    rotationMode,
    setViewportRotation,
    target,
    viewer,
  ]);

  const previousViewer = usePrevious(viewer);

  const handleCanvasClick = useCallback(
    ({ originalTarget }: CanvasClickEvent) => {
      if (!(originalTarget instanceof SVGPolygonElement)) {
        return;
      }

      if (originalTarget.classList.contains('inference-boundary-polygon')) {
        const boundaryIndex = originalTarget.getAttribute('data-inferenceboundaryindex');
        if (
          onInferenceBoundaryClickRef.current &&
          boundaryIndex !== null &&
          !observationClickDisabled.current
        ) {
          onInferenceBoundaryClickRef.current(Number(boundaryIndex));
        }
        return;
      }

      if (!originalTarget.classList.contains('observation-polygon')) {
        return;
      }

      const observationId = originalTarget.getAttribute('data-observationid');
      if (setObservationRef.current && observationId && !observationClickDisabled.current) {
        setObservationRef.current(observationId);
      }
    },
    [onInferenceBoundaryClickRef, setObservationRef]
  );

  // adds handlers to the viewer
  useEffect(() => {
    if (viewer && viewer !== previousViewer) {
      const svg = viewer.svgOverlay().node();
      // add handlers for error reporting
      viewer.addHandler('open-failed', ({ source, message }: OpenFailedEvent) => {
        notifyBugsnag(new Error(`open-failed.  ${source} : ${message}`));
      });
      viewer.addHandler('tile-load-failed', ({ tile, message }: TileLoadFailedEvent) => {
        notifyBugsnag(new Error(`tile-load-failed.  ${tile} : ${message}`));
        onTileLoadFailedRef.current?.();
      });
      viewer.addHandler('add-item-failed', ({ message }: AddItemFailedEvent) => {
        notifyBugsnag(new Error(`add-item-failed. ${message}`));
      });

      // add handlers for polygon click
      viewer.addHandler('canvas-click', handleCanvasClick);

      // Disable interaction with observations while the user is dragging the image
      viewer.addHandler('canvas-drag', () => {
        observationClickDisabled.current = true;
      });
      viewer.addHandler('canvas-drag-end', () => {
        setTimeout(() => {
          observationClickDisabled.current = false;
        }, 100);
      });

      focusLayer();
      setSvg(svg);
    }

    if (viewer && levels) {
      viewer.addHandler('tile-drawing', ({ rendered }: TileDrawingEvent) => {
        const context = rendered?.context2D;
        const image = transformImage(context)(levels as any);
        context.putImageData(image, 0, 0);
      });
    }
  }, [viewer, previousViewer, levels, focusLayer, handleCanvasClick, transformImage]);

  const prevImage = usePrevious(image);
  const prevObservations = usePrevious({ observations });

  // updates the image rendered by the viewer
  useEffect(() => {
    const prevUrl = prevImage?.signedUrl?.url ? new URL(prevImage.signedUrl.url) : undefined;
    const newUrl = image?.signedUrl?.url ? new URL(image?.signedUrl?.url) : undefined;

    if (
      viewer &&
      (prevUrl?.pathname !== newUrl?.pathname ||
        prevUrl?.searchParams.get('filter') !== newUrl?.searchParams.get('filter'))
    ) {
      transformImage?.cache?.clear?.();
      setLevels(undefined);
      setCustomRotationDeg(0);
      setRotationMode(prevRotationMode =>
        prevRotationMode === 'custom' ? 'none' : prevRotationMode
      );
      viewer.open(getTileSources(dziImage));
    }

    if (!isEqual(prevObservations?.observations, observations)) {
      focusLayer();
    }
  }, [
    dziImage,
    focusLayer,
    image?.signedUrl?.url,
    observations,
    prevImage?.signedUrl?.url,
    prevObservations,
    transformImage?.cache,
    viewer,
  ]);

  const imagePaneBox = isNewFullSizeImageViewer
    ? imageContainer?.getBoundingClientRect()
    : undefined;

  return (
    // data-private used to hide images from LogRocket: https://docs.logrocket.com/reference/dom
    <ImageContainer ref={setImageContainer} data-private={'logrocket-hide'}>
      {isThumbnail ? (
        <ThumbnailHeader $hasControlsOnly={!primaryIndicator && !thumbnailHeaderAttributes}>
          {primaryIndicator}
          <Controls
            bladeRotationTitle={bladeRotationTitle}
            onChange={handleControls}
            onClickLevels={() => undefined}
            pictureId={pictureId}
            pictureGlobalId={pictureGlobalId}
            rotationEnabled={rotationEnabled}
            value={{
              rotate: rotationMode === 'blade',
              customRotationDeg,
              customRotationActive: rotationMode === 'custom',
              filter,
              levels: defaultValue.levels,
            }}
            isThumbnail={true}
            refetch={refetch}
          />
          {thumbnailHeaderAttributes}
        </ThumbnailHeader>
      ) : (
        <Controls
          bladeRotationTitle={bladeRotationTitle}
          onChange={handleControls}
          onClickLevels={() => undefined}
          pictureId={pictureId}
          pictureGlobalId={pictureGlobalId}
          rotationEnabled={rotationEnabled}
          value={{
            rotate: rotationMode === 'blade',
            customRotationDeg,
            customRotationActive: rotationMode === 'custom',
            filter,
            levels: defaultValue.levels,
            measure: showMeasurements,
            temperature: showTemperatures,
            assetOverlay: showAssetOverlay,
            selectedInferenceDetectionTypeIds,
            edit: isEdit,
          }}
          hasHomography={hasHomography}
          inferenceFilterOptions={inferenceFilterOptions}
          refetch={refetch}
        />
      )}
      {svg &&
        createPortal(
          <ImageSvgOverlay
            assetOverlayRender={assetOverlayRender}
            deltaTMeasurementRender={deltaTMeasurementRender}
            onObservationClick={handleObservationClick}
            imagePaneBox={imagePaneBox}
            inferenceBoundaries={inferenceBoundaries}
            isNewFullSizeImageViewer={isNewFullSizeImageViewer}
            measurementsRender={measurementsRender}
            observations={observations}
            panelDetectionBoundaries={panelDetectionBoundaries}
            render={render}
            showAssetOverlay={showAssetOverlay}
            showDeltaTMeasurement={showDeltaTMeasurement}
            showInferenceOverlay={showInferenceOverlay}
            showMeasurements={showMeasurements}
            showTemperatures={showTemperatures}
            temperaturesRender={temperaturesRender}
            viewer={viewer}
          />,
          svg
        )}
    </ImageContainer>
  );
};
